import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useQuery } from 'react-apollo';

import CardDesignerCardElementColorControl from '../../../CardDesignerCardElements/CardDesignerCardElementControls/components/CardDesignerCardElementColorControl';
import Label from '../../../../../Label';
import Input from '../../../../../Input';
import Alert from '../../../../../Alert';
import ImageUpload from '../../../../../ImageUpload';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import useDismissiblePersistent from '../../../../../../../hooks/useDismissiblePersistent';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import CardDesignerAssetPicker, {
  ASSET_PICKER_TAB,
} from '../CardDesignerAssetPicker/CardDesignerAssetPicker';
import { SystemPrintAssetGroups } from '../../../../../../../graphql/queries/system-print-assets';
import {
  getBackgroundAssetElementConfig,
  getGroupSmartToggleElementConfig,
} from '../../../../../../../util/print-asset-groups';

const propTypes = {
  className: PropTypes.string,
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  defaultPricingCategoryIds: PropTypes.arrayOf(PropTypes.string),
  orientation: PropTypes.string,
};

const CardDesignerBackgroundControls = ({
  className,
  element = {},
  onElementConfigurationChange,
  defaultPricingCategoryIds,
  orientation,
}) => {
  const classes = classNames({
    [className]: !!className,
  });

  const { cardElementConfiguration } = element;
  const [color, setColor] = useState(
    cardElementConfiguration?.background_color,
  );
  const { visible: noticeVisible, onDismiss: onNoticeDismiss } =
    useDismissiblePersistent('CARD_DESIGNER_BACKGROUND_NOTICE');

  const { data: { systemPrintAssetGroups = {} } = {} } = useQuery(
    SystemPrintAssetGroups,
    {
      variables: { print_asset_group_type: PRINT_ASSET_TYPES.BACKGROUND },
      fetchPolicy: 'cache-first',
    },
  );

  const linkedGroupId = cardElementConfiguration?.print_asset_group?.id;

  // User uploads never belong to a group; drop any group link with them.
  const groupClearingConfig = linkedGroupId
    ? { print_asset_group_id: null }
    : {};

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      onElementConfigurationChange({
        element,
        elementConfig: {
          asset_image_id: assetImage.id,
          ...groupClearingConfig,
        },
      });
    },
  });

  return (
    <div className={classes}>
      <div className="card-designer-inline-color-control mb-2">
        <Label id="background-control">Background Color</Label>
        <CardDesignerCardElementColorControl
          defaultColor={cardElementConfiguration?.background_color ?? undefined}
          onChange={hex => {
            setColor(hex);
            onElementConfigurationChange({
              element,
              elementConfig: {
                background_color: hex,
              },
            });
          }}
        />
        <Input id="background-control" value={color} readOnly />
      </div>

      <Label
        id="background-image-control"
        labelTip="Backgrounds are used for graphic elements only and will not be copyright protected. Please upload images containing athletes or students to a photo box and use the Layering feature to adjust to your preferred placement."
      >
        Background Image
      </Label>
      {noticeVisible && (
        <Alert
          type="info"
          onDismiss={onNoticeDismiss}
          className="mt-2 text-primary text-extra-bold text-xs"
        >
          Upload a custom background!
        </Alert>
      )}
      <CardDesignerAssetPicker
        printAssetType={PRINT_ASSET_TYPES.BACKGROUND}
        searchPlaceholder="Search your backdrops.."
        defaultPricingCategoryIds={defaultPricingCategoryIds}
        defaultTab={
          cardElementConfiguration?.print_asset_group
            ? ASSET_PICKER_TAB.WALDO_LIBRARY
            : undefined
        }
        orientation={orientation}
        currentGroupId={linkedGroupId}
        onGroupSmartToggle={(group, enable) =>
          onElementConfigurationChange({
            element,
            elementConfig: getGroupSmartToggleElementConfig({
              group,
              enable,
              currentAssetImageId: cardElementConfiguration?.asset_image?.id,
            }),
          })
        }
        currentAssetImageId={cardElementConfiguration?.asset_image?.id}
        onAssetSelect={printAsset =>
          onElementConfigurationChange({
            element,
            elementConfig: getBackgroundAssetElementConfig({
              printAsset,
              currentGroupId: linkedGroupId,
              groups: systemPrintAssetGroups.records ?? [],
            }),
          })
        }
        uploadControl={
          <ImageUpload
            id="card-designer-background-image"
            buttonText="Upload an image"
            loading={uploading}
            disabled={uploading}
            image={
              cardElementConfiguration?.asset_image
                ? {
                    id: cardElementConfiguration.asset_image.id,
                    thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(
                      size => ({
                        size_code: size,
                        url: cardElementConfiguration.asset_image.url,
                        width: 0,
                        height: 0,
                      }),
                    ),
                  }
                : null
            }
            onInputChange={event => {
              const [file] = event.target.files;
              onUpload(file);
            }}
            onImageDismiss={() => {
              onUploadReset();
              onElementConfigurationChange({
                element,
                elementConfig: {
                  asset_image_id: null,
                  ...groupClearingConfig,
                },
              });
            }}
          />
        }
      />
    </div>
  );
};

CardDesignerBackgroundControls.propTypes = propTypes;

export default CardDesignerBackgroundControls;
